
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { tap as rxTap, startWith, take } from 'rxjs';
import { HTMLDto, ManufacturerDto, MceApiService, StringSelectDto } from '../../../core/mceapi.client.generated';
import { FormDirective } from '../../../shared/directives/form.directive';

interface PVState { manufacturer: string; range: string; module: string; }

@Component({
    selector: 'app-equipment-data-pv',
    imports: [FormDirective, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule, MatSelectModule],
    templateUrl: './equipment-data-pv.component.html',
    styleUrl: './equipment-data-pv.component.scss'
})
export class EquipmentDataPvComponent {

  private mceApiService = inject(MceApiService);

  protected readonly formValid = signal<boolean>(false);
  protected readonly formDirty = signal<boolean>(false);

  private readonly processing = signal(false);

  protected pvState = signal<PVState>({ manufacturer: '', range: '', module: '' });

  private manufacturers = toSignal(
    this.mceApiService.v1PvManufacturers().pipe(
      startWith([])),
    { initialValue: [] });

  private ranges = signal<StringSelectDto[]>([]);
  private modules = signal<StringSelectDto[]>([]);
  private datasheet = signal<HTMLDto>({html: ''});


  protected readonly viewModel = computed(() => {
    return {

      formValid: this.formValid(),
      formDirty: this.formDirty(),

      manufacturers: this.manufacturers().map((x: ManufacturerDto) => ({
        value: x.guid,
        label: x.name
      })),

      ranges: this.ranges(),
      showRanges: this.ranges().length > 0,

      modules: this.modules(),
      showModules: this.modules().length > 0,

      datasheet: this.datasheet().html,
      showDatasheet: this.datasheet().html !== '',

      showSpinner: this.processing(),
    };
  });

  protected get vm() { return this.viewModel(); }

  onManufacturerChange($event: MatSelectChange): void {
    this.processing.set(true);
    this.mceApiService.v1PvManufacturersRanges($event.value).pipe(
      take(1),
      rxTap(ranges => {
        this.pvState.update(x => ({ ...x, manufacturer: $event.value }));
        this.ranges.set(ranges);
        this.modules.set([]);
        this.datasheet.set({ html: '' });
        this.processing.set(false);
      })).subscribe();
  }


  onRangeChange($event: MatSelectChange): void {
    this.processing.set(true);
    this.mceApiService.v1PvSeriesModules($event.value).pipe(
      take(1),
      rxTap(modules => {
        this.pvState.update(x => ({ ...x, range: $event.value }));
        this.modules.set(modules);
        this.datasheet.set({html:''});
        this.processing.set(false);
      })).subscribe();
  }

  onModuleChange($event: MatSelectChange): void {
    this.processing.set(true);
    this.mceApiService.v1PvModuleDatasheetHtml($event.value).pipe(
      take(1),
      rxTap(datasheet => {
        this.pvState.update(x => ({ ...x, module: $event.value }));
        this.datasheet.set(datasheet);
        this.processing.set(false);
      })).subscribe();
  }

}
